import { strings } from "@quickpoint.me/shared";
import { type AtomEffect, atom, atomFamily, selector } from "recoil";
import type { controlsHelp } from "../displayComponents/sidebar/Hints";
import type { TourVariant } from "../lib/tour/tour";
import { ShowShare, ShowStacks } from "./stackAtoms";

export type SideBarPaneTypes =
	| typeof strings.context.hints
	| typeof strings.context.stacks
	| typeof strings.context.filter
	| typeof strings.context.files;

export type LocalStorageKeys =
	| "show_portrait"
	| "show_landscape"
	| "auth_token_ts"
	| "opened_in_tab_stories";

const localStorageEffect: (key: LocalStorageKeys) => AtomEffect<boolean> =
	(key) =>
		({ setSelf, onSet }) => {
			const savedValue = localStorage.getItem(key);
			if (savedValue) {
				setSelf(JSON.parse(savedValue));
			}

			onSet((newValue, _, isReset) => {
				isReset
					? localStorage.removeItem(key)
					: localStorage.setItem(key, JSON.stringify(newValue));
			});
		};

const stringArrayLocalStorageEffect: (
	key: LocalStorageKeys,
) => AtomEffect<string[]> =
	(key) =>
		({ setSelf, onSet }) => {
			const savedValue = localStorage.getItem(key);
			if (savedValue) {
				const uniqueValues: string[] = Array.from(new Set(JSON.parse(savedValue)));
				setSelf(uniqueValues);
			}

			onSet((newValue, _, isReset) => {
				isReset
					? localStorage.removeItem(key)
					: localStorage.setItem(key, JSON.stringify(newValue));
			});
		};

const aiExplanationStorageEffect: (key: string) => AtomEffect<string> =
	(param) =>
		({ setSelf, onSet }) => {
			const key = `ai_explanation_${param}`;
			const savedValue = localStorage.getItem(key);
			if (savedValue) {
				setSelf(savedValue);
			}

			onSet((newValue, _, isReset) => {
				isReset
					? localStorage.removeItem(key)
					: localStorage.setItem(key, newValue);
			});
		};

export const tourPositionKey = "tour_position";
const tourPositionStorageEffect: AtomEffect<number> = ({ setSelf, onSet }) => {
	const key = tourPositionKey;
	const savedValue = localStorage.getItem(key);
	if (savedValue != null) {
		setSelf(Number.parseInt(JSON.parse(savedValue)));
	}

	onSet((newValue, _, isReset) => {
		isReset
			? localStorage.removeItem(key)
			: localStorage.setItem(key, JSON.stringify(newValue));
	});
};

// An atom to track the state of code folding
export const CodeIsFolded = atom<boolean>({
	key: "CodeIsFolded",
	default: false,
});

// Track whether text formatting can be applied (for B/I/U button states)
export const CanApplyTextFormatting = atom<boolean>({
	key: "CanApplyTextFormatting",
	default: false,
});

// use a random number to prevent the atom from caching and not triggering updates
export const CodeMirrorIsReady = atom<number>({
	key: "CodeMirrorIsReady",
	default: 0,
});

// let a new user try out the system with certain features disabled
export const DemoMode = atom<boolean>({
	key: "DemoMode",
	default: false,
});


export const ShowTour = atom<boolean>({
	key: "ShowTour",
	default: false,
});

export const ShowTourSelectionAtom = atom<boolean>({
	key: "ShowTourSelectionAtom",
	default: false,
});

export const SelectedTourVariantAtom = atom<TourVariant>({
	key: "SelectedTourVariantAtom",
	default: "accessibility",
});

/** Step the tour should open on instead of 0 — set before ShowTour,
 *  consumed (and cleared) when the tour opens. Lets the AI chat jump
 *  straight to a section. */
export const PendingTourStepAtom = atom<number | null>({
	key: "PendingTourStepAtom",
	default: null,
});

export const InPresentationMode = atom<boolean>({
	key: "InPresentationMode",
	default: false,
});

export const MousedOverTextHelper = atom<keyof typeof controlsHelp | undefined>(
	{
		key: "MousedOverTextHelper",
		default: undefined,
	},
);

export const Orientation = atom<
	typeof strings.ui.portrait | typeof strings.ui.landscape
>({
	key: "Orientation",
	default: strings.ui.landscape,
});

export const OpenedInTabStories = atom<string[]>({
	key: "OpenedInTabStories",
	default: [],
	effects: [stringArrayLocalStorageEffect("opened_in_tab_stories")],
});
export const ShowPortraitPreview = atom<boolean>({
	key: "ShowPortrait",
	default: true,
	effects: [localStorageEffect("show_portrait")],
});

export const ShowLandscapePreview = atom<boolean>({
	key: "ShowLandscape",
	default: true,
	effects: [localStorageEffect("show_landscape")],
});
/**
 * Which Sidebar to show
 */
export const SideBarPane = atom<SideBarPaneTypes>({
	key: "SideBarPane",
	default: strings.context.hints,
});

export const ShowFileUploader = atom<boolean>({
	key: "ShowFileUploader",
	default: false,
});

export const ShowRefreshSpinner = atom<boolean>({
	key: "ShowRefreshSpinner",
	default: false,
});

export const SortStoriesBy = atom<"stackCfKey" | "updated" | "created">({
	key: "SortStoriesBy",
	default: "stackCfKey",
});

export const TourPositionAtom = atom<number>({
	key: "TourPositionAtom",
	default: 0,
	effects: [tourPositionStorageEffect],
});

export const ShowAiModalAtom = atom<{
	isOpen: boolean;
	mode: "prompt" | "explanation" | "generating";
	explanation?: string;
}>({
	key: "ShowAiModalAtom",
	default: { isOpen: false, mode: "prompt" },
});
export const IsMaskVisibleSelector = selector({
	key: "IsMaskVisibleSelector",
	get: ({ get }) => {
		const showFileUploader = get(ShowFileUploader);
		const showStacks = get(ShowStacks);
		const showShare = get(ShowShare);
		const showTourSelection = get(ShowTourSelectionAtom);

		return (
			showFileUploader ||
			showStacks ||
			showShare ||
			showTourSelection
		);
	},
});

export const AiExplanationAtom = atomFamily<string, string>({
	key: "AiExplanationAtom",
	default: "",
	effects: (stackCfKey) => [aiExplanationStorageEffect(stackCfKey)],
});

export const SideBarTabAtom = atom<"help" | "ai">({
	key: "SideBarTabAtom",
	default: "help",
});
